// MACHINERY V2 (Stroje) — THE THREE READINESS TIERS (M2.16c).
//
// >>> ONE DERIVATION, FOUR CONSUMERS, THIS FILE. <<<
// The katalog list card's readiness row (mobile/screenshots/16-katalog-list.png),
// the readiness legend (19-katalog-readiness.png), the katalog detail's
// readiness callout and the add sheet's tier note all name the SAME three
// levels — OEM ready / Ciastocny profil / Bez profilu. They are derived here and
// nowhere else, which is the discipline fleet/cardStates.ts and
// fleet/fleetActivity.ts already keep for their own surfaces: two components
// independently deciding the same truth is the bug class this module has shipped
// more than once.
//
// ============================================================
//  IT IS A NEW CONCEPT, AND IT IS NOT `BannerTier`
// ============================================================
// `utils/midasReadiness.ts:36` defines `BannerTier = "oem-gold" | "ai-blue" |
// null` — a DIFFERENT axis, and reusing it here would be wrong in a way that is
// easy to miss. `bannerTier()` (:217-221, personally read at commit time) gives
// "ai-blue" only for `isAiReady`, i.e. DIRECT docs or proven live capability, so
// a model whose documents arrived ONLY through the control-system fan-out comes
// back `null` — the banner's "no ribbon" answer. That model nonetheless HAS
// indexed documentation, which is exactly what the middle tier claims
// ("Dokumentacia indexovana s citaciami"), so filing it under "Bez profilu"
// would print a denial of documents the platform holds.
//
// ============================================================
//  TWO INPUTS: `oemLit` FIRST, THEN `aiProvenance` — review finding F2
// ============================================================
// `MachineryV2ReadinessPresentation.aiProvenance` is the adapter's own 1:1
// relabel of the DOC-LANE decision (readinessPresentation.ts `aiProvenanceFrom`,
// :130-150). It was read here as if it also carried the OEM fact, and it does
// not: its first line is `if (!anyDocs) return "none"`, so an OEM-partner model
// with zero indexed documents comes back "none" — provenance has nothing to
// attribute — and this table then filed it under "Bez profilu" while
// `oemDotLit()` lit its OEM badge and the "OEM ready" filter returned it.
// Filter by OEM ready on a phone, get cards labelled "No profile".
//
// >>> AND THE OBVIOUS FIX IS THE WRONG ONE. <<< Hoisting the `oemLit` test
// INSIDE `aiProvenanceFrom` would make the desktop badge tooltip assert OEM
// PROVENANCE for a model with no documents — trading a phone label bug for a
// desktop truth bug, and re-opening the split-brain that adapter's header says
// it exists to prevent. The codebase already recognises this exact state as
// distinct: `readinessSummaryParts` prints `strings.oemAvailable` rather than
// `strings.oem` for precisely `isOem && !hasDocsPart`
// (utils/midasReadiness.ts:325-327, read at commit time). So the OEM fact is
// read BESIDE the provenance, from the same one derivation, and neither reader
// is asked a question it does not answer.
//
// The provenance members still partition the NON-OEM half exactly the way the
// remaining two tiers do:
//   · "direct" / "fanout" / "unknown" -> documents exist, no OEM approval;
//   · "none"                          -> no documents at all.
// Reading that rather than re-testing raw doc fields is what keeps this tier
// unable to disagree with the capability badge's tooltip about DOCUMENTS: both
// read one value that one function produced. It also buys a compile-time
// alarm — the switch below has NO `default:`, so a sixth provenance member
// fails to build here instead of silently falling into a tier.
//
// >>> TELEMETRY IS DELIBERATELY NOT AN INPUT. <<< The brief named it as a
// candidate and the legend's own copy mentions live signals, but `telemetry` is
// a PER-MODEL fact that varies WITHIN a tier: an OEM-partner model may be
// connected or not, and both are still "OEM ready". Folding it in would let the
// tier assert something the individual model does not support. The legend's
// bullets are a static explanation of what a tier MEANS; the per-model live
// state stays where it already is, on the capability badge and in the popup.
//
// >>> `utils/midasReadiness.ts` IS NOT TOUCHED BY THIS FILE. <<< It is
// shared-util territory outside v2 and the adapter already exports everything
// the tiers need. This file imports one TYPE from the v2 adapter and nothing
// else.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import {
  MachineryV2ReadinessPresentation,
  ReadinessAiProvenance,
} from "./readinessPresentation";

/** The board's three levels, in descending order of readiness. */
export type ReadinessTier = "oem" | "partial" | "none";

/**
 * THE derivation, from TWO inputs of the one presentation object: `oemLit`
 * first, then `aiProvenance` for everything that is not OEM (review finding
 * F2 — the header explains why the OEM test cannot be folded into the
 * provenance). The switch is exhaustive over a closed enum — do not add a
 * `default:`.
 */
export const readinessTierOf = (
  presentation: MachineryV2ReadinessPresentation
): ReadinessTier => {
  // INPUT ONE — the same flag `oemDotLit()` lights the badge with and the
  // "OEM ready" filter selects on, so the label, the badge and the filter
  // cannot disagree about which models are OEM ready (F2).
  if (presentation.oemLit) return "oem";
  // INPUT TWO — what the DOCUMENTS say, for everything that is not OEM.
  const provenance: ReadinessAiProvenance = presentation.aiProvenance;
  switch (provenance) {
    case "oem":
      // Unreachable in practice: `aiProvenanceFrom` returns "oem" only when
      // `oemLit` is true, and that already returned above. It is NOT dead
      // weight — deleting the case would leave the switch non-exhaustive, and
      // with it the `never` assignment below would stop failing to compile
      // when a sixth member is added. Kept as the alarm's coverage.
      return "oem";
    case "none":
      return "none";
    case "direct":
    case "fanout":
    case "unknown":
      return "partial";
  }
  // Unreachable for a well-typed `provenance`. Kept as the compile-time alarm
  // described in the header: a new member makes this assignment fail.
  const exhaustive: never = provenance;
  return exhaustive;
};

export interface ReadinessTierDescriptor {
  tier: ReadinessTier;
  /** Dot + bullet colour. Palette CUSTOM PROPERTIES, never a hex: the module
   *  ships two palettes and these must follow both. The pinned cross-palette
   *  signal hexes in `RD` are for the OEM/MIDAS/LIVE DOTS, a different mark. */
  ink: string;
  labelKey: string;
  /** The legend's explanatory rows, in board order. */
  bulletKeys: string[];
}

/**
 * Board 19's three cards, top to bottom. The ORDER of this array is the order
 * the legend renders, so it is descending readiness — and the card row uses the
 * same table for its label and its dot, which is what stops a tier being named
 * one thing in a list and another in its own explainer.
 */
export const READINESS_TIERS: ReadinessTierDescriptor[] = [
  {
    tier: "oem",
    ink: "var(--green)",
    // REUSED, not restated: `myMachinery:oemReady` already ships in en + sk and
    // is what the desktop catalog rail calls this same level
    // (CatalogFilterRail.tsx's MIDAS READINESS section). A second Slovak
    // spelling of one platform concept inside one module is worse than a
    // one-word difference from the PNG.
    // RULED at the M2.16c review: board 16 draws the literal "OEM ready" and
    // this key stays anyway — one vocabulary per concept across surfaces beats
    // per-board fidelity. Do not "correct" this to the PNG.
    labelKey: "myMachinery:oemReady",
    bulletKeys: [
      "myMachinery:v2.catalog.tierOemB1",
      "myMachinery:v2.catalog.tierOemB2",
      "myMachinery:v2.catalog.tierOemB3",
    ],
  },
  {
    tier: "partial",
    ink: "var(--amber)",
    labelKey: "myMachinery:v2.catalog.tierPartial",
    bulletKeys: [
      "myMachinery:v2.catalog.tierPartialB1",
      "myMachinery:v2.catalog.tierPartialB2",
      "myMachinery:v2.catalog.tierPartialB3",
    ],
  },
  {
    tier: "none",
    ink: "var(--faint)",
    labelKey: "myMachinery:v2.catalog.tierNone",
    bulletKeys: [
      "myMachinery:v2.catalog.tierNoneB1",
      "myMachinery:v2.catalog.tierNoneB2",
      "myMachinery:v2.catalog.tierNoneB3",
    ],
  },
];

/** Descriptor lookup for a single model — the card row's entry point. */
export const readinessTierDescriptor = (
  presentation: MachineryV2ReadinessPresentation
): ReadinessTierDescriptor => {
  const tier = readinessTierOf(presentation);
  // `find` cannot miss: READINESS_TIERS covers all three members of the union
  // and `readinessTierOf` returns one of them. The fallback exists so the
  // return type is not `| undefined` at every call site.
  const found = READINESS_TIERS.find((t) => t.tier === tier);
  return found || READINESS_TIERS[READINESS_TIERS.length - 1];
};

/** The tier dot the card row and the legend both paint. */
export const READINESS_TIER_DOT_SIZE = 8;
